import React from 'react';

export interface UserIconProps extends React.SVGProps<SVGSVGElement> {
  /** Width/height in px (defaults to 20). */
  size?: number | string;
  /** Optional accessible name; when provided the icon is exposed to assistive tech. */
  title?: string;
  /** Stroke color (defaults to `--color-green`); pass a `--color-*` token to match other chip tones. */
  color?: string;
}

/**
 * User glyph used in the seat-usage chip. Decorative by default (aria-hidden)
 * since it is paired with the seat-count text.
 * @param props - component props (any valid SVG attribute may also be passed).
 * @returns the user icon SVG element.
 */
function UserIcon({ size = 20, className = '', title, color = 'var(--color-green)', ...rest }: UserIconProps) {
  const accessibilityProps = title
    ? { role: 'img', 'aria-label': title }
    : { 'aria-hidden': true, focusable: false };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...accessibilityProps}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <circle cx="10.0003" cy="4.99935" r="3.33333" stroke={color} strokeWidth="1.5" />
      <path
        d="M16.6663 14.584C16.6663 16.6551 16.6663 18.334 9.99967 18.334C3.33301 18.334 3.33301 16.6551 3.33301 14.584C3.33301 12.5129 6.31778 10.834 9.99967 10.834C13.6816 10.834 16.6663 12.5129 16.6663 14.584Z"
        stroke={color}
        strokeWidth="1.5"
      />
    </svg>
  );
}

/** Pure presentational icon — memoised so it never re-renders unless its props change. */
export default React.memo(UserIcon);
